// Common UI styles and conventions for the application.
// Tokens here mirror the design system in src/styles/tokens.css.
import { keyframes } from '@mui/material';

import { tokens } from '../theme';

// Color constants — the Microcumulus palette
// (night indigo = primary brand, sky blue = accent, paper = warm neutrals)
export const colors = {
  primary: {
    light: tokens.night[400],
    main: tokens.night[500],
    dark: tokens.night[700],
  },
  secondary: {
    light: tokens.sky[300],
    main: tokens.sky[500],
    dark: tokens.sky[700],
  },
  accent: {
    light: tokens.sky[300],
    main: tokens.sky[500],
    dark: tokens.sky[700],
  },
  error: {
    light: tokens.rust[300],
    main: tokens.rust[500],
    dark: tokens.rust[700],
  },
  success: {
    light: tokens.term[300],
    main: tokens.term[500],
    dark: tokens.term[700],
  },
  warning: {
    light: tokens.amber[300],
    main: tokens.amber[500],
    dark: tokens.amber[700],
  },
  background: {
    default: tokens.paper[50],
    paper: '#ffffff',
    sunken: tokens.paper[100],
    inverse: tokens.night[800],
    terminal: tokens.night[900],
    gradient: `linear-gradient(180deg, ${tokens.night[900]} 0%, ${tokens.night[700]} 100%)`,
  },
  fg: {
    primary: tokens.paper[900],
    secondary: tokens.paper[700],
    muted: tokens.paper[500],
    onDark: tokens.paper[100],
  },
  border: {
    hairline: tokens.paper[200],
    default: tokens.paper[300],
    strong: tokens.paper[500],
  },
};

// Gradient definitions — kept for API compatibility.
// The brand is solid-color-first; gradients are reserved for "night" surfaces
// and the cumulus mark. Text gradients are not part of the brand.
export const gradients = {
  primary: `linear-gradient(180deg, ${tokens.night[700]} 0%, ${tokens.night[500]} 100%)`,
  primaryHover: `linear-gradient(180deg, ${tokens.night[800]} 0%, ${tokens.night[600]} 100%)`,
  night: `linear-gradient(180deg, ${tokens.night[900]} 0%, ${tokens.night[700]} 100%)`,
  sky: `linear-gradient(180deg, ${tokens.sky[300]} 0%, ${tokens.sky[500]} 100%)`,
  // `text.style` previously produced neon gradient text. The new brand uses
  // solid night-indigo for display headings; keep the export so existing
  // call sites compile, but drop the clip-path trickery.
  text: {
    style: {
      color: tokens.night[700],
      fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
      letterSpacing: '-0.02em',
    },
  },
};

// Border radiuses — tight, infrastructural
export const borderRadius = {
  small: 0.5, // 4px
  default: 1, // 8px
  large: 1.75, // 14px
  hero: 3, // 24px
};

// Shadow tokens
export const shadows = {
  1: '0 1px 0 rgba(16, 26, 47, 0.04), 0 1px 2px rgba(16, 26, 47, 0.06)',
  2: '0 2px 4px rgba(16, 26, 47, 0.06), 0 4px 12px rgba(16, 26, 47, 0.06)',
  3: '0 6px 16px rgba(16, 26, 47, 0.08), 0 12px 28px rgba(16, 26, 47, 0.08)',
  focus: `0 0 0 3px ${tokens.sky[200]}`,
};

// Animations
export const animations = {
  gradientAnimation: keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `,
  pulseAnimation: keyframes`
    0% { opacity: 0.55; }
    50% { opacity: 1; }
    100% { opacity: 0.55; }
  `,
  progressAnimation: keyframes`
    0% { width: 20%; margin-left: 0%; }
    50% { width: 60%; margin-left: 20%; }
    100% { width: 20%; margin-left: 80%; }
  `,
};

// Common component styles
export const componentStyles = {
  card: {
    borderRadius: borderRadius.default,
    border: '1px solid',
    borderColor: colors.border.hairline,
    backgroundColor: colors.background.paper,
    boxShadow: shadows[1],
    transition: 'box-shadow 200ms cubic-bezier(.2,.8,.2,1), border-color 200ms cubic-bezier(.2,.8,.2,1)',
    '&:hover': {
      boxShadow: shadows[2],
      borderColor: colors.border.default,
    },
  },
  pageHeader: {
    p: 4,
    borderRadius: borderRadius.large,
    border: '1px solid',
    borderColor: colors.border.hairline,
    backgroundColor: colors.background.paper,
    boxShadow: shadows[1],
    mb: 4,
  },
  buttons: {
    primary: {
      borderRadius: borderRadius.small,
      backgroundColor: colors.primary.main,
      color: '#ffffff',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: colors.primary.dark,
        boxShadow: 'none',
      },
    },
    outlined: {
      borderRadius: borderRadius.small,
      borderColor: colors.border.default,
      color: colors.primary.dark,
      '&:hover': {
        borderColor: colors.primary.main,
        backgroundColor: tokens.paper[100],
      },
    },
    error: {
      borderRadius: borderRadius.small,
      borderColor: colors.error.main,
      color: colors.error.main,
      '&:hover': {
        borderColor: colors.error.dark,
        backgroundColor: 'rgba(194, 77, 48, 0.06)',
      },
    },
  },
  statusChip: {
    fontWeight: 500,
    fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
    fontSize: 12,
    maxWidth: '100%',
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  eyebrow: {
    fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    color: colors.fg.muted,
  },
};

// Loader styles
export const loaderStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    mt: { xs: 4, sm: 6, md: 10 },
  },
  progressBar: (size: 'small' | 'medium' | 'large') => {
    const barHeight = size === 'small' ? 4 : size === 'large' ? 8 : 6;
    const barWidth = size === 'small' ? '120px' : size === 'large' ? '240px' : '180px';
    return {
      width: barWidth,
      height: barHeight,
      borderRadius: barHeight,
      backgroundColor: tokens.paper[100],
      overflow: 'hidden',
      position: 'relative',
      mb: 3,
    };
  },
  progressBarInner: {
    position: 'absolute',
    height: '100%',
    borderRadius: 'inherit',
    backgroundColor: colors.secondary.main,
    animation: `${animations.progressAnimation} 1.4s cubic-bezier(.2,.8,.2,1) infinite`,
  },
  text: (_size: 'small' | 'medium' | 'large') => ({
    fontWeight: 500,
    color: colors.fg.secondary,
    fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
    fontSize: 13,
    letterSpacing: '0.04em',
  }),
  getVariant: (size: 'small' | 'medium' | 'large'): 'body1' | 'h6' | 'h4' => {
    return size === 'small' ? 'body1' : size === 'large' ? 'h4' : 'h6';
  },
};

// Cost display styles
export const costStyles = {
  container: {
    display: 'inline-block',
  },
};

const defaultExport = {
  colors,
  gradients,
  borderRadius,
  shadows,
  animations,
  componentStyles,
  loaderStyles,
  costStyles,
};

export default defaultExport;
